import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Animated, Image, Platform, ScrollView, View } from 'react-native';

import styles from './definitions-order.style';
import { useFont } from '../../../hooks/lesson-player.context';
import { useActivityContext } from '../../../hooks/use-activity.context';
import { useErrorSound } from '../../../hooks/use-error-sound';
import { SoundProvider } from '../../../hooks/use-sound-context';
import { useSuccessSound } from '../../../hooks/use-success-sound';
import { arrayShuffle } from '../../../utils/array';
import trueBeginnerStr from '../../../utils/true-beginner-str';
import { CheckButton } from '../../check-button/check-button';
import { ChoiceBubble } from '../../choice-bubble/choice-bubble';
import { ChoiceBubbleStatus } from '../../choice-bubble/choice-bubble-status.enum';
import { incorrectAnimation } from '../../choice-bubble/choice-bubble.animations';
import { ContinueButton } from '../../continue-button/continue-button';
import { IconAudioPlayerWithContext } from '../../icon-audio-player/icon-audio-player-with-context';
import { Instruction } from '../../instruction/instruction.component';
import { ThemedText } from '../../themed-text';
import { ThemedView } from '../../themed-view';

const DEFAULT_HINT_MIN_ATTEMPTS = 2;

export function DefinitionsOrder() {
  const {
    currentIndex,
    currentStep,
    currentActivity,
    goNext,
    currentStepIndex,
    completeCurrentStep,
    isCurrentStepComplete,
  } = useActivityContext();
  const fonts = useFont();
  const correctSound = useSuccessSound();
  const incorrectSound = useErrorSound();

  const xRef = useRef(new Animated.Value(0));
  const scaleRef = useRef(new Animated.Value(0));
  const questionOpacityRef = useRef(new Animated.Value(1));

  const [tries, setTries] = useState(0);
  const [isFeedbackVisible, setFeedbackVisible] = useState(false);
  const [userAnswer, setUserAnswer] = useState<(string | null)[][]>([]);

  const scale = scaleRef.current.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.1],
  });

  const correctParts = useMemo(() => {
    if (!currentStep?.chunks) {
      return [];
    }

    return currentStep.chunks
      .trim()
      .substring(2, currentStep.chunks.length - 2)
      .split(']][[')
      .map((w) => w.split(','));
  }, [currentStep]);

  const resetUserAnswer = useCallback(() => {
    setUserAnswer(() =>
      correctParts.map((w) => new Array(w.length).fill(null))
    );
  }, [correctParts]);

  useEffect(() => {
    resetUserAnswer();
    setTries(0);
    setFeedbackVisible(false);
  }, [correctParts, resetUserAnswer, currentStepIndex, currentIndex]);

  const shuffledOptions = useMemo(() => {
    return arrayShuffle(correctParts.flat());
  }, [correctParts]);

  const options = useMemo(() => {
    const possibleOptions = [...shuffledOptions];
    const answersFlat = userAnswer.flat();

    answersFlat.forEach((option) => {
      if (option === null) {
        return;
      }

      const index = possibleOptions.findIndex((answer) => answer === option);
      possibleOptions.splice(index, 1);
    });

    return possibleOptions;
  }, [shuffledOptions, userAnswer]);

  const findNextEmptyFragment = () => {
    for (let wordIndex = 0; wordIndex < userAnswer.length; wordIndex++) {
      const word = userAnswer[wordIndex];
      for (let charIndex = 0; charIndex < word.length; charIndex++) {
        const char = word[charIndex];
        if (char === null) {
          return [wordIndex, charIndex];
        }
      }
    }

    return null;
  };

  const setCharInUserAnswer = (
    wordIndex: number,
    charIndex: number,
    value: string | null
  ) => {
    const word = [
      ...userAnswer[wordIndex].slice(0, charIndex),
      value,
      ...userAnswer[wordIndex].slice(charIndex + 1),
    ];

    setUserAnswer([
      ...userAnswer.slice(0, wordIndex),
      word,
      ...userAnswer.slice(wordIndex + 1),
    ]);
  };

  const chooseOptionWithIndex = (index) => {
    const char = options[index];
    if (!char) {
      return;
    }
    const nextIndexes = findNextEmptyFragment();

    if (!nextIndexes) {
      return;
    }

    const [wordIndex, charIndex] = nextIndexes;

    setCharInUserAnswer(wordIndex, charIndex, char);
  };

  const removeAnswerWithIndexes = (wordIndex, charIndex) => {
    setCharInUserAnswer(wordIndex, charIndex, null);
  };

  const questionFadeOut = useCallback((callback?: Animated.EndCallback) => {
    Animated.timing(questionOpacityRef.current, {
      toValue: 0,
      duration: 600,
      useNativeDriver: Platform.OS !== 'web',
    }).start(callback);
  }, []);

  const questionFadeIn = useCallback(() => {
    Animated.timing(questionOpacityRef.current, {
      toValue: 1,
      duration: 400,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, []);

  const hintVisible = useMemo(() => {
    if (!currentStep?.hint) {
      return false;
    }

    if (currentStep?.maxHints) {
      return tries >= currentStep.maxHints;
    }

    return tries >= DEFAULT_HINT_MIN_ATTEMPTS;
  }, [currentStep, tries]);

  const verify = () => {
    const userAnswerStr = userAnswer.map((c) => c.join('')).join(' ');
    const isCorrect = currentStep.answer.trim() === userAnswerStr;

    if (isCorrect) {
      correctSound.play();
      questionFadeOut();
      setTimeout(() => {
        setFeedbackVisible(true);
        questionFadeIn();
        completeCurrentStep();
      }, 800);
    } else {
      setTries((x) => x + 1);
      incorrectSound.play();
      incorrectAnimation(xRef.current).start();
      setTimeout(() => {
        resetUserAnswer();
      }, 800);
    }
  };

  const translateX = xRef.current.interpolate({
    inputRange: [-1, 0, 1],
    outputRange: [-5, 0, 5],
  });

  return (
    <>
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.section}>
          <Instruction
            text={trueBeginnerStr(
              currentActivity,
              'instructions',
              currentActivity.trueBeginner
            )}
          />
          {!!currentStep?.image && (
            <Image style={styles.image} source={{ uri: currentStep?.image }} />
          )}

          <ThemedView style={styles.view}>
            <ThemedText
              type="onPrimary"
              fontSize="small"
              style={[styles.text, { ...fonts.primary.regular }]}
            >
              {trueBeginnerStr(
                currentStep,
                'text',
                currentActivity.trueBeginner
              )}
            </ThemedText>

            {isFeedbackVisible && (
              <Animated.View
                style={[styles.answer, { opacity: questionOpacityRef.current }]}
              >
                <ThemedText
                  type="onPrimary"
                  fontSize="medium"
                  style={[{ ...fonts.primary.bold }]}
                >
                  {currentStep?.answer}
                </ThemedText>
              </Animated.View>
            )}

            {!isFeedbackVisible && (
              <>
                <Animated.View
                  style={[
                    { transform: [{ translateX }, { scale }] },
                    { opacity: questionOpacityRef.current },
                  ]}
                >
                  <View style={styles.answerContainer}>
                    {userAnswer.map((word, wordIndex) => (
                      <View key={wordIndex} style={styles.word}>
                        {word.map((char, charIndex) => {
                          if (char === null) {
                            return (
                              <ChoiceBubble
                                key={`${wordIndex}-${charIndex}`}
                                style={styles.optionBubbleContainer}
                                buttonStyle={[
                                  styles.optionBubble,
                                  styles.emptyFragment,
                                ]}
                                status={ChoiceBubbleStatus.DEFAULT}
                                text=" "
                              />
                            );
                          }
                          return (
                            <ChoiceBubble
                              key={`${wordIndex}-${charIndex}`}
                              style={styles.optionBubbleContainer}
                              buttonStyle={styles.optionBubble}
                              status={ChoiceBubbleStatus.DEFAULT}
                              text={char}
                              onPress={() =>
                                removeAnswerWithIndexes(wordIndex, charIndex)
                              }
                            />
                          );
                        })}
                      </View>
                    ))}
                  </View>
                </Animated.View>
                {hintVisible && (
                  <View style={styles.hint}>
                    <SoundProvider url={currentStep?.hint}>
                      <IconAudioPlayerWithContext
                        fontSize="extraSmall"
                        text="Hint"
                      />
                    </SoundProvider>
                  </View>
                )}
              </>
            )}
          </ThemedView>

          <View style={styles.optionsContainer}>
            {options.map((option, index) => (
              <ChoiceBubble
                key={`${option}-${index}`}
                style={styles.optionBubbleContainer}
                buttonStyle={[styles.largeOptionBubble]}
                status={ChoiceBubbleStatus.DEFAULT}
                text={option}
                onPress={() => chooseOptionWithIndex(index)}
              />
            ))}
          </View>
        </View>
      </ScrollView>
      <CheckButton
        isVisible={options.length === 0 && !isCurrentStepComplete}
        onPress={verify}
      />
      {isCurrentStepComplete && <ContinueButton onPress={goNext} />}
    </>
  );
}
